import type { RemoveIndexSignature } from 'src/types/removeIndexSignature';

import { ComposerTranslation, LocaleMessageValue, VueMessageType } from 'vue-i18n';
import { RouteLocationNormalizedLoaded, Router } from 'vue-router';

import { useIdoStore } from 'src/stores/ido';
import { usePhoneCallStore } from 'src/stores/phoneCall';

import { DirectPhoneCallGatewayStrategy } from 'src/components/phoneCall/phoneCallGateway/DirectPhoneCallGatewayStrategy';
import { DirectPhoneCallGatewayWithHangupFunctionalityStrategy } from 'src/components/phoneCall/phoneCallGateway/DirectPhoneCallGatewayWithHangupFunctionalityStrategy';
import { PollingPhoneCallGatewayStrategy } from 'src/components/phoneCall/phoneCallGateway/PollingPhoneCallGatewayStrategy';
import { openDetailsAndEdit } from 'src/components/workspace/menu/functions';

import { IdoButton, IdoWindowIdoInstance } from 'src/models/ido';
import { ModuleNameEnum } from 'src/models/module/enums';

import { notifyService } from 'src/services/notify/NotifyService';

import { CommandBus } from 'src/util/command/CommandBus';
import { translatedModuleName } from 'src/util/common';
import { EventBus } from 'src/util/eventBus/EventBus';
import { normalizeForDial } from 'src/util/phoneNumber/normalizeForDial';
import { isScreenGtSm } from 'src/util/platform';

type IdoStore = ReturnType<typeof useIdoStore>;
type PhoneCallStore = ReturnType<typeof usePhoneCallStore>;

export class PhoneCallGateway {
	private readonly authenticatedUserId: number;
	private readonly commandBus: CommandBus;
	private readonly detailsContext: unknown;
	private readonly idoStore: IdoStore;
	private readonly phoneCallStore: PhoneCallStore;
	private readonly router: Router;
	private readonly eventBus: EventBus;
	private readonly shouldUseTheSplitView: boolean;
	private readonly t: ComposerTranslation<
		{
			[p: string]: LocaleMessageValue<VueMessageType>;
		},
		string,
		RemoveIndexSignature<{
			[p: string]: LocaleMessageValue<VueMessageType>;
		}>,
		never,
		string,
		string
	>;
	private readonly strategy:
		| DirectPhoneCallGatewayStrategy
		| DirectPhoneCallGatewayWithHangupFunctionalityStrategy
		| PollingPhoneCallGatewayStrategy;
	private readonly route: RouteLocationNormalizedLoaded;

	constructor(
		commandBus: CommandBus,
		detailsContext: unknown,
		phoneCallStore: PhoneCallStore,
		authenticatedUserId: number,
		router: Router,
		route: RouteLocationNormalizedLoaded,
		eventBus: EventBus,
		shouldUseTheSplitView: boolean,
		t: ComposerTranslation<
			{
				[p: string]: LocaleMessageValue<VueMessageType>;
			},
			string,
			RemoveIndexSignature<{
				[p: string]: LocaleMessageValue<VueMessageType>;
			}>,
			never,
			string,
			string
		>,
		strategy: DirectPhoneCallGatewayStrategy | PollingPhoneCallGatewayStrategy
	) {
		this.commandBus = commandBus;
		this.detailsContext = detailsContext;
		this.idoStore = useIdoStore();
		this.phoneCallStore = phoneCallStore;
		this.router = router;
		this.route = route;
		this.eventBus = eventBus;
		this.shouldUseTheSplitView = shouldUseTheSplitView;
		this.t = t;
		this.strategy = strategy;
		this.authenticatedUserId = authenticatedUserId;
	}

	public makeCallWithIdoAndInstanceId(callerName: string, number: string, idoId: number, idoInstanceId: number) {
		this.clearActiveCall();
		notifyService.info(`${this.t('phone_call_initialize_phone_call')} ${number}`);

		number = normalizeForDial(number);

		void this.strategy.makeCall(callerName, number).then((callId: null | string) => {
			if (callId !== null) {
				this.setTheNextOutgoingCallIdForACalledNumberAfterAGivenTimestamp(callId);

				void this.idoStore.linkTheAggregateToTheIdo({
					aggregateId: callId,
					commandBus: this.commandBus,
					idoId: idoId,
					idoInstanceId: idoInstanceId,
				});

				openDetailsAndEdit(
					this.router,
					this.route,
					this.detailsContext,
					this.eventBus,
					isScreenGtSm() && this.shouldUseTheSplitView,
					translatedModuleName(this.t, ModuleNameEnum.MODULE_PHONECALL),
					this.createTheAggregateId(callId),
					ModuleNameEnum.MODULE_PHONECALL,
					'module',
					{
						isACreateAction: true,
						isAManuallyCreation: true,
					}
				);
			}
		});
	}

	public makeCallWithIdoButton(callerName: string, number: string, idoButton: IdoButton) {
		this.clearActiveCall();
		notifyService.info(`${this.t('phone_call_initialize_phone_call')} ${number}`);

		number = normalizeForDial(number);

		void this.strategy.makeCall(callerName, number).then((callId: null | string) => {
			if (callId !== null) {
				this.setTheNextOutgoingCallIdForACalledNumberAfterAGivenTimestamp(callId);

				this.linkThePhonecallToTheIdos(callId, idoButton);

				openDetailsAndEdit(
					this.router,
					this.route,
					this.detailsContext,
					this.eventBus,
					isScreenGtSm() && this.shouldUseTheSplitView,
					translatedModuleName(this.t, ModuleNameEnum.MODULE_PHONECALL),
					this.createTheAggregateId(callId),
					ModuleNameEnum.MODULE_PHONECALL,
					'module',
					{
						isACreateAction: true,
						isAManuallyCreation: true,
					}
				);
			}
		});
	}

	private setTheNextOutgoingCallIdForACalledNumberAfterAGivenTimestamp(callId: string) {
		this.phoneCallStore.setTheNextOutgoingCallIdForACalledNumberAfterAGivenTimestamp({
			nextOutgoingCallIdForACalledNumberAfterAGivenTimestamp: parseInt(callId),
		});
	}

	private linkThePhonecallToTheIdos(callId: string, idoButton: IdoButton) {
		const nextOutgoingPhoneCallAggregateId = this.createTheAggregateId(callId);

		idoButton.idoWindow.idoWindowIdoInstances.forEach((idoWindowInstance: IdoWindowIdoInstance) => {
			idoWindowInstance.idoWindowIdos.forEach((idoWindowIdo) => {
				const parsedAggregateId = JSON.parse(idoWindowIdo.aggregateId) as {
					idoId: number;
					instanceId: number;
				};
				void this.idoStore.linkTheAggregateToTheIdo({
					aggregateId: nextOutgoingPhoneCallAggregateId,
					commandBus: this.commandBus,
					idoId: parsedAggregateId.idoId,
					idoInstanceId: parsedAggregateId.instanceId,
				});
			});
		});
	}

	private createTheAggregateId(callId: string) {
		return `{"moduleName":"${ModuleNameEnum.MODULE_PHONECALL}","entryId":${parseInt(
			String(callId)
		)},"userId":${this.authenticatedUserId},"sourceContainerId":0,"targetContainerId":0}`;
	}

	public endCall() {
		if ('endCall' in this.strategy) {
			this.strategy.endCall();
		}

		this.clearActiveCall();
	}

	protected clearActiveCall() {
		this.phoneCallStore.setOutgoingCallWithHangupFunctionalityCallId({
			callId: null,
		});

		this.phoneCallStore.setTheNextOutgoingCallIdForACalledNumberAfterAGivenTimestamp({
			nextOutgoingCallIdForACalledNumberAfterAGivenTimestamp: null,
		});
	}
}
